import {WalkOfShameGameAssets} from "../WalkOfShameGameAssets";
import {WalkOfShame} from "../WalkOfShame";
import {Layers, WalkOfShameLayers} from "./WalkOfShameLayers";
import {WalkOfShameGameMode} from "../server/WalkOfShameGameMode";
import {WalkOfShameSound} from "../sound/WalkOfShameSound";
import {gsap} from "gsap";

export class WalkOfShameBackground extends PIXI.Container {
    private _background: PIXI.Sprite;
    private readonly _mainBgTexture: PIXI.Texture;
    private readonly _fsBgTexture: PIXI.Texture;
    private _sfsBg: PIXI.spine.Spine;
    private _clickAbles: PIXI.Sprite[] = [];
    private _prevGunSound: string = "";
    private _prevHatSound: string = "";
    private _prevTapeSound: string = "";

    constructor() {
        super();
        this._mainBgTexture = WalkOfShame.assetLoader.getTextures(WalkOfShameGameAssets.MAIN_GAME_BACKGROUND)[0];
        this._fsBgTexture = WalkOfShame.assetLoader.getTextures(WalkOfShameGameAssets.FREE_SPIN_BACKGROUND)[0];
        this.init();

        WalkOfShameLayers.getLayer(Layers.BACKGROUND)?.addChild(this);
    }

    public updateBackground(mode: string): void {
        this._sfsBg.visible = false;
        this._sfsBg.state.clearTracks();

        if (mode === WalkOfShameGameMode.NORMAL || mode === WalkOfShameGameMode.RESPIN) {
            this._background.texture = this._mainBgTexture;

            this._clickAbles.forEach((item: PIXI.Sprite) => {
                item.visible = true;
            });
        } else if (mode === WalkOfShameGameMode.FREESPIN) {
            this._background.texture = this._fsBgTexture;
        } else {
            this._sfsBg.visible = true;
            this._sfsBg.updateTransform();
            this._sfsBg.state.setEmptyAnimation(0, 0);

            // #168
            WalkOfShameSound.soundPlayer.playTrack({
                name: WalkOfShameSound.SFS_CAR
            });

            this._sfsBg.state.setAnimation(0, "start", false);
            this._sfsBg.state.addAnimation(0, "loop", true, 3);
        }
    }

    public turnOffClickAbles(): void {
        this._clickAbles.forEach((item: PIXI.Sprite) => {
            item.visible = false;
        });
    }

    private init(): void {
        this._background = new PIXI.Sprite();
        this._background.position.set(360);
        this._background.anchor.set(0.5);

        this._sfsBg = new PIXI.spine.Spine(WalkOfShame.assetLoader.getSpineAsset("sfsBg"));
        this._sfsBg.position.set(360);
        this._sfsBg.visible = false;

        this.addChild(this._background, this._sfsBg);

        this.createClickAble(-68, 525, 74, 130, this.dragonClicked);
        this.createClickAble(-85, 310, 20, 200, this.gunClicked).rotation = -0.5;
        this.createClickAble(-68, 175, 100, 40, this.hatClicked);

        this.createClickAble(760, 480, 55, 80, this.sheepClicked);
        this.createClickAble(856, 325, 5, 140, this.whipClicked).rotation = 0.15;
        this.createClickAble(802, 163, 20, 60, this.tapeClicked).rotation = 0.2;
    }

    private createClickAble(xPos: number, yPos: number, width: number, height: number, clickCb: () => void): PIXI.Sprite {
        const item: PIXI.Sprite = new PIXI.Sprite(PIXI.Texture.EMPTY);

        item.interactive = true;
        item.buttonMode = true;
        item.position.set(xPos, yPos);
        item.anchor.set(0.5);
        item.width = width;
        item.height = height;
        item.on('pointertap', clickCb);
        item.visible = false;

        this._clickAbles.push(item);
        this.addChild(item);

        return item;
    }

    private dragonClicked(): void {
        WalkOfShameSound.soundPlayer.playTrack({
            name: WalkOfShameSound.CLICK_DRAGON
        });

        // due to scoping issue, following is duplicated in each callback.
        this.interactive = false;
        this.buttonMode = false;
        gsap.delayedCall(1, () => {
            this.interactive = true;
            this.buttonMode = true;
        });
    }

    private gunClicked(): void {
        let soundName: string = WalkOfShameSound.CLICK_GUN.next();

        while (soundName === this._prevGunSound) {
            soundName = WalkOfShameSound.CLICK_GUN.next();
        }

        WalkOfShameSound.soundPlayer.playTrack({
            name: soundName
        });
        this._prevGunSound = soundName;

        this.interactive = false;
        this.buttonMode = false;
        gsap.delayedCall(1, () => {
            this.interactive = true;
            this.buttonMode = true;
        });
    }

    private hatClicked(): void {
        let soundName: string = WalkOfShameSound.CLICK_HAT.next();

        while (soundName === this._prevHatSound) {
            soundName = WalkOfShameSound.CLICK_HAT.next();
        }

        WalkOfShameSound.soundPlayer.playTrack({
            name: soundName
        });
        this._prevHatSound = soundName;

        this.interactive = false;
        this.buttonMode = false;
        gsap.delayedCall(3, () => {
            this.interactive = true;
            this.buttonMode = true;
        });
    }

    private sheepClicked(): void {
        WalkOfShameSound.soundPlayer.playTrack({
            name: WalkOfShameSound.CLICK_SHEEP
        });

        this.interactive = false;
        this.buttonMode = false;
        gsap.delayedCall(1, () => {
            this.interactive = true;
            this.buttonMode = true;
        });
    }

    private whipClicked(): void {
        WalkOfShameSound.soundPlayer.playTrack({
            name: WalkOfShameSound.CLICK_WHIP
        });

        this.interactive = false;
        this.buttonMode = false;
        gsap.delayedCall(1, () => {
            this.interactive = true;
            this.buttonMode = true;
        });
    }

    private tapeClicked(): void {
        let soundName: string = WalkOfShameSound.CLICK_TAPE.next();

        while (soundName === this._prevTapeSound) {
            soundName = WalkOfShameSound.CLICK_TAPE.next();
        }

        WalkOfShameSound.soundPlayer.playTrack({
            name: soundName
        });
        this._prevTapeSound = soundName;

        this.interactive = false;
        this.buttonMode = false;
        gsap.delayedCall(10, () => {
            this.interactive = true;
            this.buttonMode = true;
        });
    }
}